/**
 * Inventario: consulta del libro de movimientos y verificacion contra la cache.
 *
 * Los campos de inventario en `ingredients` son una CACHE de lectura rapida. La
 * fuente de verdad es el libro `inventoryMovements`, al que solo se agregan
 * registros. Esto da un camino de reparacion ante cualquier descuadre
 * (Req. 7.8, 7.10, 18.3, 18.4).
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import {
  costoPorBase,
  cantidadSinCosto,
  recalcularDesdeMovimientos,
  compararConLibro,
  type DiferenciaInventario,
  type OrigenCosto,
} from '@/domain/inventory';
import { formatearCantidadBase } from '@/domain/units';
import type { Centavos } from '@/domain/money';
import type { Ingredient, InventoryMovement } from '../types';
import { estadoDe } from './ingredients';

// ---------------------------------------------------------------------------
// Vista del inventario
// ---------------------------------------------------------------------------

export interface RenglonInventario {
  insumo: Ingredient;
  /** Cantidad fisica, en unidad legible. */
  existencia: string;
  existenciaBase: number;
  /** Parte de la existencia que no tiene costo conocido (Req. 8.6). */
  sinCostoBase: number;
  sinCosto: string | null;
  /** Costo por unidad base. null si no hay dato del cual partir. */
  costoPorBase: number | null;
  origenCosto: OrigenCosto;
  /** Costo expresado en una unidad legible, para poder leerlo de un vistazo. */
  costoPorUnidadCompra: Centavos | null;
  /** Unidad en la que esta expresado el costo: "kg", "l", "pza", "barra"… */
  unidadCosto: string;
  valorCentavos: Centavos;
}

/** Inventario completo, ordenado por nombre. */
export async function verInventario(soloActivos = true): Promise<RenglonInventario[]> {
  const todos = await db.ingredients.toArray();
  const insumos = (soloActivos ? todos.filter((i) => i.activo) : todos).sort((a, b) =>
    a.nombre.localeCompare(b.nombre, 'es'),
  );

  return insumos.map((insumo) => describirRenglon(insumo));
}

export function describirRenglon(insumo: Ingredient): RenglonInventario {
  const estado = estadoDe(insumo);
  const { costoPorBase: unitario, origen } = costoPorBase(estado);
  const sinCostoBase = cantidadSinCosto(estado);
  const referencia = unidadDeReferencia(insumo);

  return {
    insumo,
    existenciaBase: insumo.stockBase,
    existencia: formatearCantidadBase(
      insumo.stockBase,
      insumo.dimension,
      insumo.unidadCompraPreferida,
    ),
    sinCostoBase,
    sinCosto:
      sinCostoBase > 0
        ? formatearCantidadBase(sinCostoBase, insumo.dimension, insumo.unidadCompraPreferida)
        : null,
    costoPorBase: unitario,
    origenCosto: origen,
    costoPorUnidadCompra: unitario === null ? null : Math.round(unitario * referencia.factor),
    unidadCosto: referencia.unidad,
    valorCentavos: insumo.valorCentavos,
  };
}

/**
 * Unidad en la que conviene expresar el costo.
 *
 * Para peso y volumen se usa SIEMPRE kilogramo y litro, aunque el insumo se
 * compre en gramos o mililitros.
 *
 * Motivo: un costo por gramo redondeado a centavos pierde el sentido. La cebolla
 * morada de $65 por kilo se veria como "$0.07 por gramo", y el chile poblano de
 * $54 por kilo como "$0.05". Ademas es como uno piensa los precios.
 */
function unidadDeReferencia(insumo: Ingredient): { unidad: string; factor: number } {
  // Peso y volumen se expresan en kilogramo y litro, para que la unidad del
  // costo coincida con la de la existencia mostrada. Mezclarlas obligaria a
  // hacer cuentas mentales: "906 g a $13.63 por barra" no se lee de un vistazo.
  if (insumo.dimension === 'masa') return { unidad: 'kg', factor: 1000 };
  if (insumo.dimension === 'volumen') return { unidad: 'l', factor: 1000 };

  // Lo que se cuenta conserva su unidad de compra.
  if (insumo.unidadCompraPreferida === 'docena') return { unidad: 'docena', factor: 12 };
  return { unidad: insumo.unidadCompraPreferida, factor: 1 };
}

// ---------------------------------------------------------------------------
// Libro de movimientos
// ---------------------------------------------------------------------------

export interface MovimientoLegible extends InventoryMovement {
  cantidadLegible: string;
}

/** Movimientos de un insumo, del mas reciente al mas antiguo. */
export async function movimientosDe(
  ingredientId: string,
  limite = 100,
): Promise<MovimientoLegible[]> {
  const insumo = await db.ingredients.get(ingredientId);
  const movimientos = await db.inventoryMovements
    .where('ingredientId')
    .equals(ingredientId)
    .toArray();

  return movimientos
    .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.creadoEn.localeCompare(a.creadoEn))
    .slice(0, limite)
    .map((m) => ({
      ...m,
      cantidadLegible:
        insumo === undefined
          ? String(m.cantidadBase)
          : formatearCantidadBase(m.cantidadBase, insumo.dimension, insumo.unidadCompraPreferida),
    }));
}

// ---------------------------------------------------------------------------
// Verificacion y reparacion (Req. 7.10, 18.4)
// ---------------------------------------------------------------------------

export interface VerificacionInsumo {
  insumo: Ingredient;
  diferencias: DiferenciaInventario[];
}

/**
 * Compara la cache de cada insumo contra el recalculo del libro.
 * Devuelve solo los que tienen diferencias; vacio significa que todo cuadra.
 */
export async function verificarInventario(): Promise<VerificacionInsumo[]> {
  const insumos = await db.ingredients.toArray();
  const movimientos = await db.inventoryMovements.toArray();

  const porInsumo = new Map<string, InventoryMovement[]>();
  for (const m of movimientos) {
    const lista = porInsumo.get(m.ingredientId) ?? [];
    lista.push(m);
    porInsumo.set(m.ingredientId, lista);
  }

  const problemas: VerificacionInsumo[] = [];

  for (const insumo of insumos) {
    const recalculado = recalcularDesdeMovimientos(
      porInsumo.get(insumo.id) ?? [],
      insumo.ultimoCostoPorBase,
    );
    const diferencias = compararConLibro(estadoDe(insumo), recalculado);
    if (diferencias.length > 0) problemas.push({ insumo, diferencias });
  }

  return problemas;
}

/**
 * Reescribe la cache de existencias a partir del libro.
 *
 * Es la reparacion: el libro manda. No borra ni modifica movimientos, solo
 * recalcula el resumen que se lee en pantalla.
 */
export async function repararInventario(ingredientIds?: readonly string[]): Promise<number> {
  const movimientos = await db.inventoryMovements.toArray();
  const porInsumo = new Map<string, InventoryMovement[]>();
  for (const m of movimientos) {
    const lista = porInsumo.get(m.ingredientId) ?? [];
    lista.push(m);
    porInsumo.set(m.ingredientId, lista);
  }

  const insumos = await db.ingredients.toArray();
  const objetivo =
    ingredientIds === undefined
      ? insumos
      : insumos.filter((i) => ingredientIds.includes(i.id));

  let reparados = 0;

  await enEscritura([db.ingredients], async () => {
    for (const insumo of objetivo) {
      const recalculado = recalcularDesdeMovimientos(
        porInsumo.get(insumo.id) ?? [],
        insumo.ultimoCostoPorBase,
      );
      if (compararConLibro(estadoDe(insumo), recalculado).length === 0) continue;

      await db.ingredients.update(insumo.id, {
        stockBase: recalculado.stockBase,
        stockValuadoBase: recalculado.stockValuadoBase,
        valorCentavos: recalculado.valorCentavos,
      });
      reparados++;
    }
  });

  return reparados;
}

// ---------------------------------------------------------------------------
// Resumen
// ---------------------------------------------------------------------------

export interface ResumenInventario {
  insumos: number;
  conExistencia: number;
  sinCostoConocido: number;
  valorTotalCentavos: Centavos;
}

export async function resumenInventario(): Promise<ResumenInventario> {
  const insumos = (await db.ingredients.toArray()).filter((i) => i.activo);

  return {
    insumos: insumos.length,
    conExistencia: insumos.filter((i) => i.stockBase > 0).length,
    sinCostoConocido: insumos.filter((i) => cantidadSinCosto(estadoDe(i)) > 0).length,
    valorTotalCentavos: insumos.reduce((suma, i) => suma + i.valorCentavos, 0),
  };
}
